import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Grid,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import toast from "react-hot-toast";
import { get, post } from "../../utility/fetch2";
import debounce from "lodash.debounce";
import GhostTextCompletion from "../UI/TextPrediction";
import { Trash2Icon } from "lucide-react";

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        maxWidth: '1000px',
        width: '100%',
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
}));

function AddMoreTreatment({ open, onClose, visit, id, fetchData, treatment, patientId, data, repeatedDiagnosis, setRepeatedDiagnosis }) {
    const [carePlan, setCarePlan] = useState("");
    const [diagnosis, setDiagnosis] = useState(treatment?.diagnosis || "");
    const [medications, setMedications] = useState([]);
    const [otherMedications, setOtherMedications] = useState([]);
    const [newOtherMedication, setNewOtherMedication] = useState("");
    const [loading, setLoading] = useState(false);
    const [treatmentCategories, setTreatmentCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(1);
    const [admissionStatus, setAdmissionStatus] = useState("Not To Be Admitted");
    const [selectedMedication, setSelectedMedication] = useState(null); // State for React Select
    const [medicationOptions, setMedicationOptions] = useState([]); // State for fetched medication options
    const [hmo, setHmo] = useState(null);
    const [sugesstPayload, setSuggPayload] = useState({ medications: [] });


    const routesOfAdministration = [
        { id: 1, name: "Orally" },
        { id: 2, name: "Sublingual" },
        { id: 3, name: "Topical" },
        { id: 4, name: "Inhalation" },
        { id: 5, name: "Suppository" },
        { id: 6, name: "IV" },
        { id: 7, name: "IM" },
        { id: 8, name: "Subcut" },
        { id: 9, name: "Intradermal" },
        { id: 10, name: "PerRectum" },
        { id: 11, name: "PerVagina" },
        { id: 12, name: "Implant" },
    ];

    const administrationFrequencies = [
        { id: 1, name: "Immediately" },
        { id: 2, name: "As needed" },
        { id: 3, name: "Once daily" },
        { id: 4, name: "Twice a day" },
        { id: 5, name: "Three times a day" },
        { id: 6, name: "Four times a day" },
        { id: 7, name: "At night" },
        { id: 8, name: "Morning" },
        { id: 9, name: "Evening" },
        { id: 10, name: "Every 24 hours" },
        { id: 11, name: "Every 12 hours" },
        { id: 12, name: "Every 8 hours" },
        { id: 13, name: "Every 6 hours" },
        { id: 14, name: "Every 4 hours" },
        { id: 15, name: "Every 3 hours" },
        { id: 16, name: "Every 2 hours" },
        { id: 17, name: "Every hour" },
        { id: 18, name: "Every 2 months" },
        { id: 19, name: "Every 3 months" },
        // Every 3 months
    ];


    console.log(visit, treatment)

    const fetchTreatmentCategory = async () => {
        try {
            const response = await get("/patients/get-all-categories");
            setTreatmentCategories(response);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        setDiagnosis(repeatedDiagnosis);
        console.log(repeatedDiagnosis);
    }, [repeatedDiagnosis]);

    const drugMeasurementUnits = [
        { id: 1, name: "Milligrams", symbol: "mg" },
        { id: 2, name: "Grams", symbol: "g" },
        { id: 3, name: "Micrograms", symbol: "µg" },
        { id: 4, name: "Milliliters", symbol: "mL" },
        { id: 5, name: "Liters", symbol: "L" },
        { id: 6, name: "Units", symbol: "U" },
        { id: 7, name: "Puffs" },
        { id: 8, name: "Sprays" },
        { id: 9, name: "Drops" },
        { id: 10, name: "Patch" },
        { id: 11, name: "Bottle" },
        { id: 12, name: "Transdermal System" },
        { id: 13, name: "Tablet" },
        { id: 14, name: "Capsule" },
        { id: 15, name: "Suppository" },
        { id: 16, name: "Scoop" },
        { id: 17, name: "Sachet" },
        { id: 18, name: "Ampoule" },
        { id: 19, name: "Vial" },
        { id: 20, name: "Injection Pen" },
        { id: 21, name: "Enema" },
        { id: 22, name: "Ounces", symbol: "oz" },
        { id: 23, name: "Teaspoon", symbol: "tsp" },
        { id: 24, name: "Tablespoon", symbol: "tbsp" },
        { id: 25, name: "Milliequivalents", symbol: "mEq" },
        { id: 26, name: "International Units", symbol: "IU" }
    ];


    // Fetch Medications from API with Filter Query (Debounced)
    const fetchMedications = async (
        filterOn = "name",
        filterQuery = " ",
        pageNumber = 1,
        itemsPerPage = 10
    ) => {
        try {
            const response = await get(
                `/pharmacyinventory/filter-list/${filterOn}/${filterQuery}/${pageNumber}/${itemsPerPage}`
            );
            const data = response?.resultList;
            console.log(response);
            // Map the response to a format usable by React Select
            const options = data?.map((med) => ({
                value: med?.id, // or med.code if you have it
                label: med?.productName, // Adjust this based on your response structure
            }));

            setMedicationOptions(options); // Set options for the dropdown
            setSelectedMedication(null); // Clear selection after fetching data
        } catch (error) {
            console.log(error);
        }
    };

    const handleTranscript = (transcript) => {
        setCarePlan(carePlan + transcript);
    };

    const fetchPatientHMO = async () => {
        try {
            const response = await get(`/hmo/get-patient-hmo/${patientId}`);
            console.log(response.data);
            setHmo(response?.data[0]);
        } catch (error) {
            console.log(error);
        }
    };

    // Function to update the suggestPayload
    const updateSuggestPayload = () => {
        const allMedications = [
            ...medications.map((med) => med.name),
            ...otherMedications.map((med) => med.name),
        ];
        if (allMedications.length === 0) {
            setSuggPayload({ medications: [] });
            return;
        } else {
            setSuggPayload({ medications: allMedications });
        }
    };

    // Update suggestPayload whenever medications or otherMedications change
    useEffect(() => {
        updateSuggestPayload();
    }, [medications, otherMedications]);

    const addMedicationFromDropdown = () => {
        if (selectedMedication) {
            setMedications((prev) => [
                ...prev,
                {
                    name: selectedMedication.label,
                    pharmacyInventoryId: selectedMedication.value, // Save the medication ID (value) here
                    quantity: "",
                    frequency: "",
                    duration: "",
                },
            ]);
            setSelectedMedication(null); // Clear selection after adding
        } else {
            toast.error("Please select a valid medication");
        }
    };

    const addOtherMedication = () => {
        if (newOtherMedication) {
            setOtherMedications((prev) => [
                ...prev,
                {
                    name: newOtherMedication,
                    quantity: 0,
                    frequency: 0,
                    duration: 0,
                },
            ]);
            setNewOtherMedication("");
        } else {
            toast.error("Please enter the other medication name");
        }
    };

    const removeMedication = (index, type) => {
        if (type === "medications") {
            const updatedMedications = [...medications];
            updatedMedications.splice(index, 1);
            setMedications(updatedMedications);
        } else if (type === "otherMedications") {
            const updatedOtherMedications = [...otherMedications];
            updatedOtherMedications.splice(index, 1);
            setOtherMedications(updatedOtherMedications);
        }
    };

    const handleMedicationChange = (
        index,
        field,
        value,
        type = "medications"
    ) => {
        const updatedMedications = [
            ...(type === "medications" ? medications : otherMedications),
        ];

        let updatedValue = value;
        if (["duration", "quantity"].includes(field)) {
            updatedValue = parseInt(value, 10) || 0;
        }

        updatedMedications[index] = {
            ...updatedMedications[index],
            [field]: updatedValue,
        };

        if (type === "medications") {
            setMedications(updatedMedications);
        } else {
            setOtherMedications(updatedMedications);
        }
    };
    const addTreatment = async () => {
        if (diagnosis === "" || carePlan === "") {
            toast.error("Please fill in all fields");
            return;
        }

        const dateOfVisit = new Date(visit?.dateOfVisit).toISOString();
        // setLoading(true);

        // Prepare the medications payload with pharmacyInventoryId
        const formattedMedications = medications.map((med) => ({
            pharmacyInventoryId: med.pharmacyInventoryId,
            quantity: med.quantity,
            strength: med.frequency,
            administrationFrequency: +med.administrationFrequency || 1,
            routeOfAdministration: +med.routeOfAdministration || 1,
            duration: med.duration,

            drugStrengthUnit: +med.drugStrengthUnit,
        }));

        // Prepare the otherMedications payload
        const formattedOtherMedications = otherMedications.map((med) => ({
            name: med.name,
            quantity: med.quantity,
            strength: med.frequency,
            administrationFrequency: +med.administrationFrequency || 1,
            routeOfAdministration: +med.routeOfAdministration || 1,
            duration: med.duration,
            drugStrengthUnit: +med.drugStrengthUnit || 1,
        }));

        // Construct the payload in the required format
        const payload = {
            patientId: patientId,
            treatmentId: treatment?.id,
            dateOfVisit: dateOfVisit,
            treatmentCategoryId: +selectedCategoryId,
            diagnosis,
            medications: formattedMedications,
            otherMedications: formattedOtherMedications,
            carePlan,
            isAdmitted: admissionStatus === "Admitted",
            hmoId: hmo?.hmoProviderId,
            hmoPackageId: hmo?.hmoPackageId,
        };

        console.log(payload);
        // return
        // if (createTreatment) {
        //   createTreatment && createTreatment(payload);
        //   return;
        // }

        try {
            const res = await post(`/patients/${patientId}/Treatment/${treatment?.id}/add-additional-prescription`, payload,); // Send payload to the endpoint
            console.log(res);
            if (res === "Successful") {

                toast.success("Treatment added successfully");
                onClose();
            } else {
                toast.error("Error adding prescription");
            }
        } catch (error) {
            toast.error("Error adding treatment");
            console.log(error);
        }
        setLoading(false);
    };

    // Debounced Search Function for Medications
    const debouncedFetchMedications = useCallback(
        debounce((query) => {
            fetchMedications("name", query); // Fetch medications based on typed query
        }, 300), // 300ms debounce
        []
    );

    // Handle input change in React Select's input field for searching medications
    const handleInputChange = (inputValue) => {
        debouncedFetchMedications(inputValue); // Trigger debounced API call
    };

    useEffect(() => {
        fetchTreatmentCategory();
        fetchPatientHMO();
        fetchMedications();
    }, []);

    return (
        <StyledDialog open={true} onClose={onClose} fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Add Treatment</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Treatment Category */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Treatment Category</InputLabel>
                            <Select
                                value={selectedCategoryId}
                                onChange={(e) => setSelectedCategoryId(e.target.value)}
                            >
                                {treatmentCategories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Diagnosis */}
                    <Grid item xs={12}>
                        <GhostTextCompletion
                            label="Patient Diagnosis"
                            name="diagnosis"
                            value={diagnosis}
                            handleChange={(e) => setDiagnosis(e.target.value)}
                        />
                    </Grid>

                    {/* Care Plan */}
                    <Grid item xs={12}>
                        <GhostTextCompletion
                            label="Add Care Plan"
                            name="carePlan"
                            value={carePlan}
                            handleChange={(e) => setCarePlan(e.target.value)}
                        />
                    </Grid>

                    {/* Admission Status */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Admission Status</InputLabel>
                            <Select
                                value={admissionStatus}
                                onChange={(e) => setAdmissionStatus(e.target.value)}
                            >
                                <MenuItem value="Admitted">To Be Admitted</MenuItem>
                                <MenuItem value="Not To Be Admitted">Not To Be Admitted</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Medications Selection */}
                    <Grid item xs={12}>
                        <Box mb={2}>
                            <Select
                                options={medicationOptions}
                                value={selectedMedication}
                                onChange={setSelectedMedication}
                                onInputChange={handleInputChange}
                                placeholder="Select a medication"
                                isClearable
                            />
                            <Button
                                variant="contained"
                                sx={{ mt: 1, bgcolor: '#17621A' }}
                                onClick={addMedicationFromDropdown}
                            >
                                Add Medication
                            </Button>
                        </Box>
                    </Grid>

                    {/* Medications Table */}
                    {medications.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Medications
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>S/N</StyledTableCell>
                                            <StyledTableCell>Medication</StyledTableCell>
                                            <StyledTableCell>Quantity</StyledTableCell>
                                            <StyledTableCell>Strength</StyledTableCell>
                                            <StyledTableCell>Units</StyledTableCell>
                                            <StyledTableCell>Frequency</StyledTableCell>
                                            <StyledTableCell>Duration</StyledTableCell>
                                            <StyledTableCell>Route</StyledTableCell>
                                            <StyledTableCell>Action</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {medications.map((med, index) => (
                                            <TableRow key={index}>
                                                <StyledTableCell>{index + 1}</StyledTableCell>
                                                <StyledTableCell>{med.name}</StyledTableCell>
                                                <StyledTableCell>
                                                    <input
                                                        type="number"
                                                        className="input-field-table"
                                                        value={med.quantity}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "quantity",
                                                                e.target.value,
                                                                "medications"
                                                            )
                                                        }
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <input
                                                        // type="text"
                                                        className="input-field-table"
                                                        // value={med.frequency}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "frequency",
                                                                e.target.value,
                                                                "medications"
                                                            )
                                                        }
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <select
                                                        className="input-field-table"
                                                        value={med.drugStrengthUnit || ""}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "drugStrengthUnit",
                                                                e.target.value,
                                                                "medications"
                                                            )
                                                        }
                                                    >
                                                        {drugMeasurementUnits.map((freq) => (
                                                            <option key={freq.id} value={freq.id}>
                                                                {freq.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <select
                                                        className="input-field-table"
                                                        value={med.administrationFrequency || ""}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "administrationFrequency",
                                                                e.target.value,
                                                                "medications"
                                                            )
                                                        }
                                                    >
                                                        {administrationFrequencies.map((freq) => (
                                                            <option key={freq.id} value={freq.id}>
                                                                {freq.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <input
                                                        type="number"
                                                        className="input-field-table"
                                                        value={med.duration}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "duration",
                                                                e.target.value,
                                                                "medications"
                                                            )
                                                        }
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <select
                                                        className="input-field-table"
                                                        value={med.routeOfAdministration || ""}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "routeOfAdministration",
                                                                e.target.value,
                                                                "medications"
                                                            )
                                                        }
                                                    >
                                                        {routesOfAdministration.map((route) => (
                                                            <option key={route.id} value={route.id}>
                                                                {route.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <DeleteIcon
                                                        className="text-red pointer"
                                                        onClick={() => removeMedication(index, "medications")}
                                                    />
                                                </StyledTableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    )}

                    {/* Input for adding Other Medications */}
                    <Grid item xs={12}>
                        <Box display="flex" gap={2}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Enter other medication"
                                value={newOtherMedication}
                                onChange={(e) => setNewOtherMedication(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                sx={{ bgcolor: '#17621A' }}
                                onClick={addOtherMedication}
                            >
                                Add Other Medication
                            </Button>
                        </Box>
                    </Grid>

                    {otherMedications.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Other Medications
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>S/N</StyledTableCell>
                                            <StyledTableCell>Medication</StyledTableCell>
                                            <StyledTableCell>Quantity</StyledTableCell>
                                            <StyledTableCell>Strength</StyledTableCell>
                                            <StyledTableCell>Units</StyledTableCell>
                                            <StyledTableCell>Frequency</StyledTableCell>
                                            <StyledTableCell>Duration</StyledTableCell>
                                            <StyledTableCell>Route</StyledTableCell>
                                            <StyledTableCell>Action</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {otherMedications.map((med, index) => (
                                            <TableRow key={index}>
                                                <StyledTableCell>{index + 1}</StyledTableCell>
                                                <StyledTableCell>{med.name}</StyledTableCell>
                                                <StyledTableCell>
                                                    <input
                                                        type="number"
                                                        className="input-field-table"
                                                        value={med.quantity}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "quantity",
                                                                e.target.value,
                                                                "otherMedications"
                                                            )
                                                        }
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <input
                                                        // type="text"
                                                        className="input-field-table"
                                                        // value={med.frequency}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "frequency",
                                                                e.target.value,
                                                                "otherMedications"
                                                            )
                                                        }
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <select
                                                        className="input-field-table"
                                                        value={med.drugStrengthUnit || ""}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "drugStrengthUnit",
                                                                e.target.value,
                                                                "otherMedications"
                                                            )
                                                        }
                                                    >
                                                        {drugMeasurementUnits.map((freq) => (
                                                            <option key={freq.id} value={freq.id}>
                                                                {freq.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <select
                                                        className="input-field-table"
                                                        value={med.administrationFrequency || ""}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "administrationFrequency",
                                                                e.target.value,
                                                                "otherMedications"
                                                            )
                                                        }
                                                    >
                                                        {administrationFrequencies.map((freq) => (
                                                            <option key={freq.id} value={freq.id}>
                                                                {freq.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <input
                                                        type="number"
                                                        className="input-field-table"
                                                        value={med.duration}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "duration",
                                                                e.target.value,
                                                                "otherMedications"
                                                            )
                                                        }
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <select
                                                        className="input-field-table"
                                                        value={med.routeOfAdministration || ""}
                                                        onChange={(e) =>
                                                            handleMedicationChange(
                                                                index,
                                                                "routeOfAdministration",
                                                                e.target.value,
                                                                "otherMedications"
                                                            )
                                                        }
                                                    >
                                                        {routesOfAdministration.map((route) => (
                                                            <option key={route.id} value={route.id}>
                                                                {route.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <DeleteIcon
                                                        className="text-red pointer"
                                                        onClick={() =>
                                                            removeMedication(index, "otherMedications")
                                                        }
                                                    />
                                                </StyledTableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    )}

                    {/* <div className="m-t-20">
                        <Suggestions payload={sugesstPayload} patientId={visit?.patientId} />
                    </div> */}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    onClick={addTreatment}
                    disabled={loading}
                    sx={{
                        bgcolor: '#17621A',
                        '&:hover': {
                            bgcolor: '#124E15'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Add Treatment'}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
}

export default AddMoreTreatment;
